import { Balance, OrderBook, TokenInfo, TokenStats, Trade } from '../types';
import { AssetWebSocketService, AssetWebSocketConfig } from './assetWebSocket';
import Decimal from 'decimal.js';

export type AssetUpdateCallback = () => void;

const MAX_TRADES = 200;

export class AssetActor {
    private asset: TokenInfo;
    private orderBook: OrderBook | null = null;
    private trades: Trade[] = [];
    private balance: Balance | null = null;
    private tokenStats: TokenStats | null = null;
    private updateCallbacks: Set<AssetUpdateCallback> = new Set();
    private isActive: boolean = false;
    private wsService: AssetWebSocketService | null = null;
    private isConnected: boolean = false;
    
    // Performance optimization: cache references to avoid unnecessary re-renders
    private cachedTradesReference: Trade[] | null = null;

    constructor(asset: TokenInfo) {
        this.asset = asset;
    }

    // Actor lifecycle - actors stay connected but only active actor triggers UI updates
    async activate(): Promise<void> {
        console.log(`Activating AssetActor for ${this.asset.symbol}`);
        this.isActive = true;
        
        // Connect WebSocket if not already connected
        if (!this.wsService) {
            await this.connectWebSocket();
        }
        
        console.log(`AssetActor for ${this.asset.symbol} activated`);
    }

    async deactivate(): Promise<void> {
        console.log(`Deactivating AssetActor for ${this.asset.symbol}`);
        this.isActive = false;
        
        // Keep WebSocket connected to maintain state!
        // Only disconnect when actor is destroyed
        console.log(`AssetActor for ${this.asset.symbol} deactivated (WebSocket stays connected)`);
    }

    // Destroy actor completely (disconnect WebSocket)
    async destroy(): Promise<void> {
        console.log(`Destroying AssetActor for ${this.asset.symbol}`);
        this.isActive = false;
        this.disconnectWebSocket();
        console.log(`AssetActor for ${this.asset.symbol} destroyed`);
    }

    isActorActive(): boolean {
        return this.isActive;
    }

    isWebSocketConnected(): boolean {
        return this.isConnected;
    }

    // State getters - return cached references for performance
    getAsset(): TokenInfo {
        return this.asset;
    }

    getOrderBook(): OrderBook | null {
        // react uses object instance equality to determine what to re-render, as this.orderBook is a
        // new intnace on every handleOrderBookUpdate we can return the instance here and if it has been
        // updated then react will re-render
        return this.orderBook;
    }

    getTrades(): Trade[] {
	// In handleTradeUpdate we keep the same array reference for this.trades but that means that
        // react would never realise that the trades had been appended to. We therefore clone the array
	// here and reset the clone in handleTradeUpdate.
        if (this.cachedTradesReference === null) {
            this.cachedTradesReference = [...this.trades]; 
        }
        return this.cachedTradesReference;
    }

    getBalance(): Balance | null {
	// as with getOrderBook, we'll get a new instance of this object from the update so we can just
	// return it here without worrying about caching
	return this.balance;
    }

    getTokenStats(): TokenStats | null {
	return this.tokenStats;
    }

    // Message handlers - ALL actors process messages, but only active actors trigger UI updates
    private handleOrderBookUpdate(orderBook: OrderBook): void {
        // Convert string decimals to Decimal objects
        const convertedOrderBook: OrderBook = {
            ...orderBook,
            bids: orderBook.bids.map(level => ({
                price: new Decimal(level.price.toString()),
                quantity: new Decimal(level.quantity.toString()),
            })),
            asks: orderBook.asks.map(level => ({
                price: new Decimal(level.price.toString()),
                quantity: new Decimal(level.quantity.toString()),
            })),
            midPrice: new Decimal(orderBook.midPrice.toString()),
            spread: new Decimal(orderBook.spread.toString()),
            timestamp: orderBook.timestamp,
        };

        this.orderBook = convertedOrderBook;
        
        // Only notify subscribers (trigger UI updates) if this actor is active
        if (this.isActive) {
            this.notifySubscribers();
            console.debug(`Order book updated for active actor: ${this.asset.symbol}`);
        } else {
            console.debug(`Order book updated for inactive actor: ${this.asset.symbol} (no UI update)`);
        }
    }

    private handleTradeUpdate(trade: Trade): void {
        const convertedTrade: Trade = {
            ...trade,
            price: new Decimal(trade.price.toString()),
            quantity: new Decimal(trade.quantity.toString()),
            timestamp: convertTimestamp(trade.timestamp),
        };

        // Efficient insertion: add to front and maintain order without full sort
        this.trades.unshift(convertedTrade);
        
        // Keep only the most recent trades and ensure proper ordering
        while (this.trades.length > MAX_TRADES) {
            this.trades.pop();
        }
        
        // Only sort if we detect ordering issues (rare case) or duplicate trades
        if (this.trades.length > 1) {
            const firstTrade = this.trades[0];
            const secondTrade = this.trades[1];
            if (firstTrade.timestamp.getTime() < secondTrade.timestamp.getTime() || firstTrade.id === secondTrade.id) {
	        // on disconnect and reconnect we'll be sent recent trades - some of which we might already have seen 
                // so we de-duplicate the array by trade ID and then sort by time
                console.warn(`Trade ordering issue detected for ${this.asset.symbol}, de-duplicating and re-sorting`);
		this.trades = this.trades.filter((trade, i, arr) => arr.findIndex(t => t.id === trade.id) === i);
                this.trades.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
            }
        }
        
        // Invalidate cached reference since trades array changed
        this.cachedTradesReference = null;
        
        // Only notify subscribers (trigger UI updates) if this actor is active
        if (this.isActive) {
            this.notifySubscribers();
            console.debug(`Trade added to active actor: ${this.asset.symbol}`, {
                id: convertedTrade.id,
                timestamp: convertedTrade.timestamp.toISOString(),
                price: convertedTrade.price.toString(),
                totalTrades: this.trades.length
            });
        } else {
            console.debug(`Trade added to inactive actor: ${this.asset.symbol} (no UI update)`, {
                id: convertedTrade.id,
                totalTrades: this.trades.length
            });
        }
    }

    private handleBalanceUpdate(balance: Balance): void {
	this.balance = {
	    ...balance,
	    balance: new Decimal(balance.balance.toString()),
            timestamp: convertTimestamp(balance.timestamp),
	};
	if (this.isActive) {
	    this.notifySubscribers();
	}
    }

    private handleTokenStatsUpdate(stats: TokenStats): void {
	this.tokenStats = {
	    ...stats,
            totalVolume24h: new Decimal(stats.totalVolume24h.toString()),
            calculatedAt: convertTimestamp(stats.calculatedAt),
	};
	if (this.isActive) {
	    this.notifySubscribers();
	}
    }

    // WebSocket management - connect once and stay connected
    private async connectWebSocket(): Promise<void> {
        if (this.wsService) {
            return; // Already connected
        }

        const config: AssetWebSocketConfig = {
            mint: this.asset.mint,
        };

        this.wsService = new AssetWebSocketService(config, {
            onOrderBookUpdate: (orderBook) => this.handleOrderBookUpdate(orderBook),
            onTradeUpdate: (trade) => this.handleTradeUpdate(trade),
            onBalanceUpdate: (balance) => this.handleBalanceUpdate(balance),
            onTokenStatsUpdate: (stats) => this.handleTokenStatsUpdate(stats),
            onConnectionChange: (connected) => {
                this.isConnected = connected;
                // Only notify if active
                if (this.isActive) {
                    this.notifySubscribers();
                }
                console.log(`WebSocket connection for ${this.asset.symbol}: ${connected ? 'connected' : 'disconnected'}`);
            },
            onError: (error) => {
                console.error(`WebSocket error for ${this.asset.symbol}:`, error);
            },
        });

        try {
            await this.wsService.connect();
        } catch (error) {
            console.error(`Failed to connect WebSocket for ${this.asset.symbol}:`, error);
        }
    }

    private disconnectWebSocket(): void {
        if (this.wsService) {
            this.wsService.disconnect();
            this.wsService = null;
        }
        this.isConnected = false;
    }

    // Subscription management
    subscribe(callback: AssetUpdateCallback): () => void {
        this.updateCallbacks.add(callback);
        
        // Return unsubscribe function
        return () => {
            this.updateCallbacks.delete(callback);
        };
    }

    private notifySubscribers(): void {
        this.updateCallbacks.forEach(callback => {
            try {
                callback();
            } catch (error) {
                console.error(`Error in asset actor callback for ${this.asset.symbol}:`, error);
            }
        });
    }

    // Load initial data from REST API (only when first created)
    async loadInitialData(): Promise<void> {
        try {
            const { apiService } = await import('../services/api');
            const { orderBook: initialOrderBook } = await apiService.getOrderBook(this.asset.mint);
            this.handleOrderBookUpdate(initialOrderBook);
        } catch (error) {
            console.error(`Failed to load initial data for ${this.asset.symbol}:`, error);
        }
    }
}

function convertTimestamp(t: Date | String): Date {
    // Convert string data to proper types with robust timestamp handling
    if (t instanceof Date) {
        return t;
    } else if (typeof t === 'string') {
        return new Date(t);
    } else {
        return new Date(); // Fallback to current time
    }
}

// Asset Actor Manager - manages all asset actors with persistent connections
export class AssetActorManager {
    private actors: Map<string, AssetActor> = new Map();
    private activeActor: AssetActor | null = null;

    // Create or get an actor for an asset
    getOrCreateActor(asset: TokenInfo): AssetActor {
        const symbol = asset.symbol;
        
        if (!this.actors.has(symbol)) {
            const actor = new AssetActor(asset);
            this.actors.set(symbol, actor);
            console.log(`Created new AssetActor for ${symbol}`);
            
            // Immediately connect and load initial data
            this.initializeActor(actor);
        }

        return this.actors.get(symbol)!;
    }

    // Initialize actor with connection and initial data
    private async initializeActor(actor: AssetActor): Promise<void> {
        try {
            // Connect WebSocket (but don't activate for UI updates yet)
            await actor['connectWebSocket']();
            
            // Load initial data
            await actor.loadInitialData();
            
            console.log(`Initialized actor for ${actor.getAsset().symbol} with persistent connection`);
        } catch (error) {
            console.error(`Failed to initialize actor for ${actor.getAsset().symbol}:`, error);
        }
    }

    // Switch active actor (for UI rendering)
    async setActiveActor(asset: TokenInfo): Promise<AssetActor> {
        const newActor = this.getOrCreateActor(asset);

        // Deactivate previous actor (stops UI updates but keeps connection)
        if (this.activeActor && this.activeActor !== newActor) {
            await this.activeActor.deactivate();
        }

        // Activate new actor (enables UI updates)
        await newActor.activate();
        this.activeActor = newActor;

        return newActor;
    }

    getActiveActor(): AssetActor | null {
        return this.activeActor;
    }

    // Get all actors (for debugging)
    getAllActors(): AssetActor[] {
        return Array.from(this.actors.values());
    }

    // Cleanup all actors (destroy connections)
    async cleanup(): Promise<void> {
        for (const actor of Array.from(this.actors.values())) {
            await actor.destroy();
        }
        this.actors.clear();
        this.activeActor = null;
    }
} 
